import { ChangeDetectionStrategy, Component, effect, ElementRef, inject, input, OnInit, signal, viewChildren } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { AviationHullForm } from '../../../models/coverages/base-coverage.model'
import { AVIATION_HULL_PANELS, GENERAL_PANEL } from '../../../models/coverages/coverage-panels.model'
import { SubCoveragePanelsComponent } from '../sub-coverage-panels/sub-coverage-panels.component'
import { Accordion, AccordionContent, AccordionHeader, AccordionPanel } from 'primeng/accordion'
import { AlbertCheckboxComponent } from '@shared/components/albert-checkbox/albert-checkbox.component'
import { AlbertInputComponent } from '@shared/components/albert-input/albert-input.component'
import { InsuredBaseForm } from '../../insured-base-form.directive'
import { AlbertPeriodComponent } from '@shared/components/albert-period/albert-period.component'
import { AlbertOverrideWrapper } from '@shared/components/albert-override-wrapper/albert-override-wrapper'
import { DetailviewNavigationService } from '@services/detailview-navigation.service'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { distinctUntilChanged, startWith } from 'rxjs'
import { Checkbox } from 'primeng/checkbox'

/**
 * Aviation Hull coverage form: the General panel (period, spares and hull deductible flags) and one
 * panel per displayed hull sub-coverage.
 *
 * @example
 * <albert-aviation-hull-form [form]="aviationHullForm" [isCreateMode]="false" [mainIndex]="2" />
 */
@Component({
  selector: 'albert-aviation-hull-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    SubCoveragePanelsComponent,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    AlbertCheckboxComponent,
    AlbertInputComponent,
    AlbertPeriodComponent,
    AlbertOverrideWrapper,
    Checkbox
  ],
  templateUrl: './aviation-hull-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AviationHullFormComponent extends InsuredBaseForm<AviationHullForm> implements OnInit {
  /** Coverage form group, owned by the insured detailview */
  public override readonly form = input.required<FormGroup<AviationHullForm>>()
  /** Position of this coverage in the detailview's sections, used to match nav targets */
  public readonly mainIndex = input<number>()
  /** Whether "Spares Jointly For" is ticked, which shows the spares twin insured input */
  public readonly isSparesJointlyForChecked = signal(false)
  /** Whether "Hull Deductible Jointly For" is ticked, which shows the hull deductible twin insured input */
  public readonly isHullDeductibleJointlyForChecked = signal(false)
  /** Section navigation of the detailview, used for the panels' nav indexes */
  public readonly nav = inject(DetailviewNavigationService)
  /** Shows the override, or the Origami value while there is none. Changing it sets the override. */
  protected readonly sparesEngineTestRunOverride = new FormControl(false, { nonNullable: true })

  /** Accordion value of the General panel */
  protected readonly generalPanel = GENERAL_PANEL
  /** Displayed hull sub-coverage panels */
  protected readonly panels = AVIATION_HULL_PANELS

  private readonly generalPanelTarget = viewChildren<ElementRef<HTMLElement>>('subPanel')

  constructor() {
    super()
    this.setupPanelNavigationEffects(
      this.nav,
      this.mainIndex,
      [this.generalPanel, ...this.panels.map(panel => panel.key)],
      this.generalPanelTarget
    )

    // The override checkbox is not bound through the form, so it follows edit rights on its own.
    effect(() => {
      if (this.canEdit()) this.sparesEngineTestRunOverride.enable({ emitEvent: false })
      else this.sparesEngineTestRunOverride.disable({ emitEvent: false })
    })
  }

  /**
   * Wires the General panel's dependent fields: the twin-insured inputs and the Spares Engine Test
   * Run override checkbox.
   */
  public ngOnInit(): void {
    const form = this.form()

    form.controls.general.controls.sparesJointlyFor.controls.localValue.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      distinctUntilChanged()
    ).subscribe(val => {
      this.isSparesJointlyForChecked.set(val ?? false)
    })

    // Both subscriptions end with the component via takeUntilDestroyed; the form outlives it.
    const sparesEngineTestRun = form.controls.general.controls.sparesEngineTestRun
    sparesEngineTestRun.valueChanges.pipe(
      startWith(null),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      const { localValue, externalValue } = sparesEngineTestRun.getRawValue()
      this.sparesEngineTestRunOverride.setValue(localValue ?? externalValue ?? false, { emitEvent: false })
    })

    this.sparesEngineTestRunOverride.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(checked => {
      // Marked dirty first: the autosave only picks up a change it sees on an already dirty form.
      sparesEngineTestRun.controls.localValue.markAsDirty()
      sparesEngineTestRun.controls.localValue.setValue(checked)
    })

    form.controls.general.controls.hullDeductibleJointlyFor.controls.localValue.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      distinctUntilChanged()
    ).subscribe(val => {
      this.isHullDeductibleJointlyForChecked.set(val ?? false)
    })
  }

  protected override getFormGroup(): FormGroup<AviationHullForm> {
    return this.form()
  }
}
